import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { 
  Search, MapPin, Star, Phone, Filter, User, X, SlidersHorizontal,
  Shield, CheckCircle2, Building2, Stethoscope,
  AlertCircle, RefreshCw, ExternalLink
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Skeleton } from '../components/ui/skeleton';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '../components/ui/sheet';
import { SearchableCombobox } from '../components/SearchableCombobox';
import SEOHead from '../components/SEOHead';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import useGeoLocation from '../hooks/useGeoLocation';
import { searchDoctors, suggestSpecialties } from '../services/searchApi';
import { SEO_IMAGES, getSpecialtySeoImagePath } from '../utils/seo';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const GEO_STORAGE_KEY = 'bd_geo';

function getVisitorLocation() {
  try {
    const data = JSON.parse(window.localStorage.getItem(GEO_STORAGE_KEY) || 'null');
    return {
      visitor_city: data?.city || '',
      visitor_state: data?.state || '',
    };
  } catch {
    return { visitor_city: '', visitor_state: '' };
  }
}

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [doctors, setDoctors] = useState([]);
  const [specialties, setSpecialties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalResults, setTotalResults] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [suggestedSpecialties, setSuggestedSpecialties] = useState([]);
  const [safetyAlert, setSafetyAlert] = useState('');
  const [emptyStateSuggestion, setEmptyStateSuggestion] = useState(null);
  const [searchError, setSearchError] = useState('');

  // Filter states
  const [symptom, setSymptom] = useState(searchParams.get('symptom') || '');
  const [selectedSpecialty, setSelectedSpecialty] = useState(searchParams.get('specialty_id') || '');
  const [specialtyOpen, setSpecialtyOpen] = useState(false);
  const [mobileSpecialtyOpen, setMobileSpecialtyOpen] = useState(false);

  // Cascading: Operadora -> Plano
  const [operators, setOperators] = useState([]);
  const [selectedOperator, setSelectedOperator] = useState(null);
  const [operatorPlans, setOperatorPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(searchParams.get('plan') || '');
  const [operatorOpen, setOperatorOpen] = useState(false);
  const [planOpen, setPlanOpen] = useState(false);
  const [stateOpen, setStateOpen] = useState(false);
  const [cityOpen, setCityOpen] = useState(false);
  const [mobileOperatorOpen, setMobileOperatorOpen] = useState(false);
  const [mobilePlanOpen, setMobilePlanOpen] = useState(false);
  const [mobileStateOpen, setMobileStateOpen] = useState(false);
  const [mobileCityOpen, setMobileCityOpen] = useState(false);
  const [loadingPlans, setLoadingPlans] = useState(false);

  // Cascading: Estado -> Cidade
  const [states, setStates] = useState([]);
  const [selectedState, setSelectedState] = useState(searchParams.get('state') || '');
  const [cities, setCities] = useState([]);
  const [selectedCity, setSelectedCity] = useState(searchParams.get('city') || '');
  const [loadingCities, setLoadingCities] = useState(false);

  // Other filters
  const [minRating, setMinRating] = useState(searchParams.get('min_rating') || '');
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  // Refinement filters (client-side filtering on loaded results)
  const [filterRating, setFilterRating] = useState('');
  const [filterHasPhone, setFilterHasPhone] = useState(false);

  const specialtyLabel = searchParams.get('specialty_name') || specialties.find((item) => String(item.id) === String(selectedSpecialty))?.name || '';
  const locationLabel = [selectedCity, selectedState].filter(Boolean).join(', ');
  const searchTitle = specialtyLabel && locationLabel
    ? `${specialtyLabel} em ${locationLabel} | BuscaDoutor`
    : specialtyLabel
      ? `${specialtyLabel} | BuscaDoutor`
      : locationLabel
        ? `Médicos e clínicas em ${locationLabel} | BuscaDoutor`
        : 'Buscar médicos, clínicas e especialidades | BuscaDoutor';
  const searchDescription = specialtyLabel && locationLabel
    ? `Encontre ${specialtyLabel} em ${locationLabel}, veja informações profissionais e escolha o atendimento mais adequado para sua necessidade.`
    : 'Busque médicos, clínicas, especialidades, convênios e cidades no BuscaDoutor com informações organizadas para sua decisão.';
  const canonicalQuery = new URLSearchParams();
  if (specialtyLabel) canonicalQuery.set('specialty_name', specialtyLabel);
  if (selectedState) canonicalQuery.set('state', selectedState);
  if (selectedCity) canonicalQuery.set('city', selectedCity);
  const searchCanonical = canonicalQuery.toString() ? `/buscar?${canonicalQuery.toString()}` : '/buscar';
  const searchImage = specialtyLabel ? getSpecialtySeoImagePath(specialtyLabel) : SEO_IMAGES.doctors;
  const [filterHasPlans, setFilterHasPlans] = useState(false);

  // Client-side filtered results
  const filteredDoctors = useMemo(() => {
    let result = doctors;
    if (filterRating) {
      const min = parseFloat(filterRating);
      result = result.filter(d => (d.rating_avg || 0) >= min);
    }
    if (filterHasPlans) {
      result = result.filter(d => d.accepted_health_plans && d.accepted_health_plans.length > 0);
    }
    if (filterHasPhone) {
      result = result.filter(d => d.phone && d.phone.trim());
    }
    return result;
  }, [doctors, filterRating, filterHasPlans, filterHasPhone]);

  // Helper: format city/state display
  const formatLocation = (city, state) => {
    return [city, state].filter(Boolean).map(s => 
      s.length <= 2 ? s.toUpperCase() : s.charAt(0).toUpperCase() + s.slice(1).toLowerCase()
    ).join(' - ');
  };

  const initialLoadDone = useRef(false);
  const lastLoggedSearch = useRef('');
  const searchLogIdRef = useRef(null);
  const initialCity = useRef(searchParams.get('city') || '');
  const initialPlan = useRef(searchParams.get('plan') || '');
  const selectedStateRef = useRef(searchParams.get('state') || '');
  const resultsScrollRef = useRef(null);

  // Geo-location auto-fill — only applies when URL did not specify state/city
  // and the user has not picked anything yet. URL params always win.
  const { detected, persistManual } = useGeoLocation();
  const pendingAutoCityRef = useRef(null);
  const geoAutoStateAppliedRef = useRef(false);

  useEffect(() => {
    fetchFiltersData();
  }, []);

  // Keep state ref in sync for URL param comparison
  useEffect(() => {
    selectedStateRef.current = selectedState;
  }, [selectedState]);

  // Sync local filter state from URL params on navigation
  // (handles when user navigates from Home -> Search with different params)
  useEffect(() => {
    const urlState = searchParams.get('state') || '';
    const urlCity = searchParams.get('city') || '';

    setSelectedSpecialty(searchParams.get('specialty_id') || '');
    setSymptom(searchParams.get('symptom') || '');
    setMinRating(searchParams.get('min_rating') || '');

    if (urlState !== selectedStateRef.current) {
      initialCity.current = urlCity;
      setSelectedState(urlState);
    } else {
      setSelectedCity(urlCity);
    }
  }, [searchParams]);

  // Set/sync operator from URL (handles navigation with different operators)
  useEffect(() => {
    const opName = searchParams.get('operator');
    if (opName && operators.length > 0) {
      if (!selectedOperator || selectedOperator.name.toLowerCase() !== opName.toLowerCase()) {
        const found = operators.find(o => o.name.toLowerCase() === opName.toLowerCase());
        if (found) {
          initialPlan.current = searchParams.get('plan') || initialPlan.current;
          setSelectedOperator(found);
        }
      }
    } else if (!opName && selectedOperator) {
      setSelectedOperator(null);
    }
  }, [operators, searchParams]);

  // Fetch plans when operator changes
  useEffect(() => {
    if (selectedOperator) {
      setLoadingPlans(true);
      const planToRestore = initialPlan.current;
      initialPlan.current = '';
      setSelectedPlan('');
      axios.get(`${API_URL}/api/public/ans/operators/${selectedOperator.registro_ans}/plans`)
        .then(res => {
          const plans = res.data.plans || [];
          setOperatorPlans(plans);
          if (planToRestore && plans.includes(planToRestore)) {
            setSelectedPlan(planToRestore);
          }
        })
        .catch(() => setOperatorPlans([]))
        .finally(() => setLoadingPlans(false));
    } else {
      setOperatorPlans([]);
      setSelectedPlan('');
    }
  }, [selectedOperator]);

  // Fetch cities when state changes
  useEffect(() => {
    if (selectedState) {
      setLoadingCities(true);
      const cityToRestore = initialCity.current;
      initialCity.current = '';
      setSelectedCity('');
      const ax = axios.create({ timeout: 15000 });
      let retried = false;
      const fetchCities = () => {
        ax.get(`${API_URL}/api/public/cities?state=${selectedState}`)
          .then(res => {
            const cityList = (res.data || []).sort((a, b) => (a.city || '').localeCompare(b.city || '', 'pt-BR'));
            if (cityList.length === 0 && !retried) {
              retried = true;
              setTimeout(fetchCities, 1500);
              return;
            }
            setCities(cityList);
            if (cityToRestore && cityList.some(c => c.city === cityToRestore)) {
              setSelectedCity(cityToRestore);
            } else if (pendingAutoCityRef.current) {
              const wanted = pendingAutoCityRef.current;
              pendingAutoCityRef.current = null;
              const match = cityList.find(c => (c.city || '').toLowerCase() === wanted.toLowerCase());
              if (match) setSelectedCity(match.city);
            }
            setLoadingCities(false);
          })
          .catch(() => {
            if (!retried) {
              retried = true;
              setTimeout(fetchCities, 1500);
            } else {
              setCities([]);
              setLoadingCities(false);
            }
          });
      };
      fetchCities();
    } else {
      setCities([]);
      setSelectedCity('');
    }
  }, [selectedState]);

  useEffect(() => {
    fetchDoctors();
  }, [searchParams, currentPage]);

  // Auto-apply detected state when the user has not chosen anything via URL
  // or interaction. Honors URL > manual > auto precedence.
  useEffect(() => {
    if (geoAutoStateAppliedRef.current) return;
    if (!detected?.state) return;
    if (selectedState) return;                          // URL/manual already set state
    if (searchParams.get('state')) return;              // double-check URL
    if (!states || states.length === 0) return;
    const match = states.find((s) => (s.uf || '').toUpperCase() === detected.state.toUpperCase());
    if (!match) return;
    geoAutoStateAppliedRef.current = true;
    pendingAutoCityRef.current = detected.city || null;
    setSelectedState(match.uf);
  }, [detected, states, selectedState, searchParams]);

  const fetchFiltersData = async (isRetry = false) => {
    const ax = axios.create({ timeout: 15000 });
    try {
      const [specialtiesRes, statesRes, operatorsRes] = await Promise.all([
        ax.get(`${API_URL}/api/public/specialties`).catch(() => ({ data: [], _failed: true })),
        ax.get(`${API_URL}/api/public/states`).catch(() => ({ data: [], _failed: true })),
        ax.get(`${API_URL}/api/public/ans/operators`).catch(() => ({ data: [] })),
      ]);
      setSpecialties((specialtiesRes.data || []).sort((a, b) => (a.name || '').localeCompare(b.name || '', 'pt-BR')));
      setStates((statesRes.data || []).sort((a, b) => (a.name || '').localeCompare(b.name || '', 'pt-BR')));
      setOperators(operatorsRes.data || []);

      if (!isRetry && (specialtiesRes._failed || statesRes._failed || (specialtiesRes.data || []).length === 0 || (statesRes.data || []).length === 0)) {
        setTimeout(() => fetchFiltersData(true), 2000);
      }
    } catch (error) {
    }
  };

  const fetchDoctors = async () => {
    setLoading(true);
    setDoctors([]);
    setTotalResults(0);
    setTotalPages(1);
    setSuggestedSpecialties([]);
    setSafetyAlert('');
    setEmptyStateSuggestion(null);
    setSearchError('');
    const specId = searchParams.get('specialty_id') || '';
    const specName = searchParams.get('specialty_name') || (specId ? (specialties.find(s => s.id === specId)?.name || '') : '');
    const rawQuery = searchParams.get('symptom') || searchParams.get('query') || '';
    const attendanceType = '';

    try {
      if (rawQuery && !specId && !specName) {
        const response = await suggestSpecialties({
          query: rawQuery,
          state: searchParams.get('state') || '',
          city: searchParams.get('city') || '',
          operator: searchParams.get('operator') || '',
          plan: searchParams.get('plan') || '',
          client: 'web',
        });
        const analysis = response.analysis || {};
        setSuggestedSpecialties(analysis.source === 'llm' ? (response.suggestedSpecialties || []) : []);
        setSafetyAlert(response.safetyAlert?.message || '');
        setEmptyStateSuggestion(null);
        initialLoadDone.current = true;
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }

      if (!specId && !specName) {
        setLoading(false);
        initialLoadDone.current = true;
        return;
      }

      const response = await searchDoctors({
        selectedSpecialtyId: specId,
        selectedSpecialty: specName,
        originalQuery: rawQuery,
        state: searchParams.get('state') || '',
        city: searchParams.get('city') || '',
        operator: searchParams.get('operator') || '',
        plan: searchParams.get('plan') || '',
        attendanceType,
        page: currentPage,
        limit: 12,
        client: 'web',
      });

      const pagination = response.pagination || {};
      const resultRows = response.results || [];
      setDoctors(resultRows);
      setTotalResults(pagination.total || resultRows.length || 0);
      setTotalPages(pagination.totalPages || 1);
      setSuggestedSpecialties(response.suggestedSpecialties || []);
      setSafetyAlert(response.safetyAlert?.message || response.safetyAlert || '');
      setEmptyStateSuggestion(response.emptyStateSuggestion || null);
      window.scrollTo({ top: 0, behavior: 'smooth' });

      const searchKey = `${specId}|${specName}|${rawQuery}|${searchParams.get('city') || ''}|${searchParams.get('state') || ''}|${searchParams.get('operator') || ''}|${searchParams.get('plan') || ''}|${attendanceType}`;
      if (searchKey !== lastLoggedSearch.current) {
        lastLoggedSearch.current = searchKey;
        axios.post(`${API_URL}/api/public/log-search`, {
          source: 'search',
          specialty: specName,
          specialty_id: specId,
          symptom: rawQuery,
          operator: searchParams.get('operator') || '',
          plan: searchParams.get('plan') || '',
          state: searchParams.get('state') || '',
          city: searchParams.get('city') || '',
          results_count: pagination.total || resultRows.length || 0,
        }).then((logRes) => {
          searchLogIdRef.current = logRes.data?.log_id;
        }).catch(() => {});
      }
    } catch (error) {
      setDoctors([]);
      setTotalResults(0);
      setTotalPages(1);
      setSearchError('Não foi possível buscar profissionais agora. Tente novamente em instantes.');
    } finally {
      setLoading(false);
      initialLoadDone.current = true;
    }
  };
  const triggerWebSearch = () => {
    fetchDoctors();
  };

  const applyFilters = useCallback(() => {
    const params = new URLSearchParams();
    if (symptom) params.set('symptom', symptom);
    if (selectedSpecialty && selectedSpecialty !== 'all') {
      params.set('specialty_id', selectedSpecialty);
      const selected = specialties.find((item) => item.id === selectedSpecialty);
      if (selected?.name) params.set('specialty_name', selected.name);
    }
    if (selectedOperator) params.set('operator', selectedOperator.name);
    if (selectedPlan) params.set('plan', selectedPlan);
    if (selectedState) params.set('state', selectedState);
    if (selectedCity) params.set('city', selectedCity);
    if (minRating && minRating !== 'all') params.set('min_rating', minRating);
    setSearchParams(params);
    setCurrentPage(1);
  }, [symptom, selectedSpecialty, selectedOperator, selectedPlan, selectedState, selectedCity, minRating, setSearchParams, specialties]);

  const clearFilters = useCallback(() => {
    setSymptom('');
    setSelectedSpecialty('');
    setSelectedOperator(null);
    setSelectedPlan('');
    setSelectedState('');
    setSelectedCity('');
    setMinRating('');
    setSearchParams({});
    setCurrentPage(1);
  }, [setSearchParams]);

  const startNewSearch = useCallback(() => {
    clearFilters();
    setFilterRating('');
    setFilterHasPlans(false);
    setFilterHasPhone(false);
    setMobileFiltersOpen(true);
    requestAnimationFrame(() => {
      resultsScrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
      window.scrollTo({ top: 0, behavior: 'smooth' });
      document.getElementById('search-page-top')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }, [clearFilters]);

  const hasActiveFilters = symptom || (selectedSpecialty && selectedSpecialty !== 'all') || selectedOperator || selectedPlan || selectedState || selectedCity || (minRating && minRating !== 'all');
  const shouldShowResultCount = Boolean(
    searchParams.get('specialty_id') ||
    searchParams.get('specialty_name') ||
    searchParams.get('symptom') ||
    searchParams.get('operator') ||
    searchParams.get('plan') ||
    searchParams.get('state') ||
    searchParams.get('city') ||
    (searchParams.get('min_rating') && searchParams.get('min_rating') !== 'all')
  );

  // Shared filter render function (NOT a component - avoids remount/focus loss)
  const renderFilters = (isMobile = false) => (
    <div className="space-y-5">
      {/* Especialista - SearchableCombobox from DB */}
      <div>
        <label className="text-sm font-medium text-slate-700 mb-2 block">Especialista <span className="text-red-400">*</span></label>
        <SearchableCombobox
          open={isMobile ? mobileSpecialtyOpen : specialtyOpen}
          onOpenChange={isMobile ? setMobileSpecialtyOpen : setSpecialtyOpen}
          items={specialties}
          selectedValue={selectedSpecialty && selectedSpecialty !== 'all' ? selectedSpecialty : null}
          onSelect={(spec) => setSelectedSpecialty(spec.id)}
          onClear={() => setSelectedSpecialty('')}
          placeholder="Selecione a especialidade"
          searchPlaceholder="Buscar especialidade..."
          emptyMessage="Nenhuma especialidade encontrada."
          icon={Stethoscope}
          displayValue={selectedSpecialty && selectedSpecialty !== 'all' ? (specialties.find(s => s.id === selectedSpecialty)?.name || searchParams.get('specialty_name') || 'Carregando...') : null}
          getItemKey={(s) => s.id}
          getItemLabel={(s) => s.name}
          isSelected={(s) => selectedSpecialty === s.id}
          testId={isMobile ? "mobile-specialty-select" : "filter-specialty"}
          className="h-10 rounded-lg"
        />
      </div>

      {/* Sintoma ou Doença - Free text, optional */}
      <div>
        <label className="text-sm font-medium text-slate-700 mb-2 block">Sintoma ou Doença <span className="text-slate-400 font-normal">(opcional)</span></label>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            type="text"
            placeholder="Ex: dor de cabeça, diabetes..."
            value={symptom}
            onChange={(e) => setSymptom(e.target.value)}
            className="pl-10"
            data-testid={isMobile ? "mobile-symptom-input" : "filter-symptom-input"}
          />
        </div>
      </div>

      {/* Operadora - Searchable Combobox */}
      <div>
        <label className="text-sm font-medium text-slate-700 mb-2 block">Operadora</label>
        <SearchableCombobox
          open={isMobile ? mobileOperatorOpen : operatorOpen}
          onOpenChange={isMobile ? setMobileOperatorOpen : setOperatorOpen}
          items={operators}
          selectedValue={selectedOperator}
          onSelect={(op) => setSelectedOperator(op)}
          onClear={() => setSelectedOperator(null)}
          placeholder="Todas operadoras"
          searchPlaceholder="Buscar operadora..."
          emptyMessage="Nenhuma operadora encontrada."
          icon={Shield}
          displayValue={selectedOperator?.name}
          getItemKey={(op) => op.registro_ans}
          getItemLabel={(op) => op.name}
          isSelected={(op) => selectedOperator?.registro_ans === op.registro_ans}
          testId={isMobile ? "mobile-operator-select" : "filter-operator"}
          className="h-10 rounded-lg"
        />
      </div>

      {/* Plano - Searchable Combobox, depends on Operadora */}
      <div>
        <label className="text-sm font-medium text-slate-700 mb-2 block">Plano</label>
        <SearchableCombobox
          open={isMobile ? mobilePlanOpen : planOpen}
          onOpenChange={isMobile ? setMobilePlanOpen : setPlanOpen}
          items={operatorPlans}
          selectedValue={selectedPlan}
          onSelect={(plan) => setSelectedPlan(plan)}
          onClear={() => setSelectedPlan('')}
          placeholder={selectedOperator ? "Digite o plano..." : "Selecione operadora"}
          searchPlaceholder="Digite o nome do plano..."
          emptyMessage="Nenhum plano encontrado."
          icon={CheckCircle2}
          disabled={!selectedOperator || loadingPlans}
          displayValue={loadingPlans ? "Carregando..." : selectedPlan || null}
          getItemKey={(plan) => plan}
          getItemLabel={(plan) => plan}
          isSelected={(plan) => selectedPlan === plan}
          testId={isMobile ? "mobile-plan-select" : "filter-plan"}
          className="h-10 rounded-lg"
        />
      </div>

      {/* Estado - Searchable Combobox */}
      <div>
        <label className="text-sm font-medium text-slate-700 mb-2 block">Estado <span className="text-red-400">*</span></label>
        <SearchableCombobox
          open={isMobile ? mobileStateOpen : stateOpen}
          onOpenChange={isMobile ? setMobileStateOpen : setStateOpen}
          items={states}
          selectedValue={selectedState && selectedState !== 'all' ? selectedState : null}
          onSelect={(s) => {
            geoAutoStateAppliedRef.current = true;
            pendingAutoCityRef.current = null;
            setSelectedState(s.uf);
            persistManual({ state: s.uf, city: null });
          }}
          onClear={() => setSelectedState('')}
          placeholder="Todos estados"
          searchPlaceholder="Buscar estado..."
          emptyMessage="Nenhum estado encontrado."
          icon={MapPin}
          displayValue={selectedState && selectedState !== 'all' ? (states.find(s => s.uf === selectedState)?.name || selectedState) + ` (${selectedState})` : null}
          getItemKey={(s) => s.uf}
          getItemLabel={(s) => `${s.name} (${s.uf})`}
          getItemSearchValue={(s) => `${s.name} ${s.uf}`}
          isSelected={(s) => selectedState === s.uf}
          testId={isMobile ? "mobile-state-select" : "filter-state"}
          className="h-10 rounded-lg"
        />
      </div>

      {/* Cidade - Searchable Combobox, depends on Estado */}
      <div>
        <label className="text-sm font-medium text-slate-700 mb-2 block">Cidade <span className="text-red-400">*</span></label>
        <SearchableCombobox
          open={isMobile ? mobileCityOpen : cityOpen}
          onOpenChange={isMobile ? setMobileCityOpen : setCityOpen}
          items={cities}
          selectedValue={selectedCity && selectedCity !== 'all' ? selectedCity : null}
          onSelect={(c) => {
            setSelectedCity(c.city);
            persistManual({ state: selectedState, city: c.city });
          }}
          onClear={() => setSelectedCity('')}
          placeholder={(selectedState && selectedState !== 'all') ? "Buscar cidade..." : "Selecione estado"}
          searchPlaceholder="Digite o nome da cidade..."
          emptyMessage="Nenhuma cidade encontrada."
          icon={Building2}
          disabled={!selectedState || selectedState === 'all' || loadingCities}
          displayValue={loadingCities ? "Carregando..." : (selectedCity && selectedCity !== 'all' ? selectedCity : (searchParams.get('city') || null))}
          getItemKey={(c) => c.city}
          getItemLabel={(c) => c.city}
          isSelected={(c) => selectedCity === c.city}
          testId={isMobile ? "mobile-city-select" : "filter-city"}
          className="h-10 rounded-lg"
        />
      </div>

      {/* Rating Filter */}
      <div>
        <label className="text-sm font-medium text-slate-700 mb-2 block">Avaliação mínima</label>
        <Select value={minRating} onValueChange={setMinRating}>
          <SelectTrigger data-testid="filter-rating">
            <SelectValue placeholder="Qualquer avaliação" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Qualquer avaliação</SelectItem>
            <SelectItem value="4.5">4.5+ estrelas</SelectItem>
            <SelectItem value="4">4+ estrelas</SelectItem>
            <SelectItem value="3.5">3.5+ estrelas</SelectItem>
            <SelectItem value="3">3+ estrelas</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-3 pt-4">
        <Button
          onClick={() => {
            const hasSpecialty = selectedSpecialty && selectedSpecialty !== 'all';
            const hasSymptom = symptom && symptom.trim().length >= 3;
            if (!hasSpecialty && !hasSymptom) {
              alert('Descreva uma queixa ou selecione uma especialidade.');
              return;
            }
            if (hasSpecialty && (!selectedState || selectedState === 'all' || !selectedCity || selectedCity === 'all')) {
              alert('Selecione o estado e a cidade para buscar.');
              return;
            }
            applyFilters();
            if (isMobile) setMobileFiltersOpen(false);
          }}
          className="flex-1 bg-primary"
          data-testid={isMobile ? "mobile-apply-filters-btn" : "apply-filters-btn"}
        >
          {selectedSpecialty && selectedSpecialty !== 'all' ? 'Buscar médicos' : 'Encontrar especialidade'}
        </Button>
        {hasActiveFilters && (
          <Button
            variant="outline"
            onClick={() => { clearFilters(); if (isMobile) setMobileFiltersOpen(false); }}
            data-testid="clear-filters-btn"
          >
            <X className="w-4 h-4" />
          </Button>
        )}
      </div>
    </div>
  );

  const DoctorCard = ({ doctor }) => {
    const hasPlans = doctor.accepted_health_plans && doctor.accepted_health_plans.length > 0;
    const registrationType = String(doctor.professional_registration_type || (doctor.crm ? 'crm' : '')).toUpperCase();
    const registrationNumber = doctor.professional_registration_number || doctor.crm;
    const registrationUf = doctor.professional_registration_uf || doctor.crm_state;
    const normalizedCity = (doctor.city || '').toLowerCase();
    const normalizedSelectedCity = (selectedCity || '').toLowerCase();
    const selectedPlanMatch = selectedPlan && hasPlans && doctor.accepted_health_plans.some((plan) => (plan || '').toLowerCase() === selectedPlan.toLowerCase());
    const profileSignals = [registrationNumber, doctor.phone, doctor.address || doctor.provider_name, doctor.photo, hasPlans].filter(Boolean).length;
    const matchReasons = [
      doctor.specialty_name ? 'Especialidade compat\u00edvel' : null,
      normalizedSelectedCity && normalizedCity === normalizedSelectedCity
        ? 'Atende em ' + selectedCity
        : (doctor.city ? 'Atende em ' + formatLocation(doctor.city, doctor.state) : null),
      selectedPlanMatch ? 'Aceita ' + selectedPlan : (hasPlans ? 'Conv\u00eanios informados' : null),
      registrationNumber ? `${registrationType || 'Registro'} informado` : null,
      doctor.phone ? 'Contato dispon\u00edvel' : null,
    ].filter(Boolean).slice(0, 4);
    const logProfileEvent = (eventType) => {
      axios.post(`${API_URL}/api/public/profile-event`, {
        doctor_id: doctor.id,
        event_type: eventType,
        source: 'search_card',
        ...getVisitorLocation(),
      }).catch(() => {});
    };
    return (
      <Link
        to={doctor.slug ? `/${doctor.slug}` : `/medico/${doctor.id}`}
        className={`group bg-white rounded-xl border ${doctor.is_premium ? 'border-amber-200 ring-1 ring-amber-100' : 'border-[#DDE8E4]'} hover:border-primary/30 transition-all hover:shadow-[0_12px_34px_rgba(28,78,66,0.08)] overflow-hidden`}
        data-testid={`doctor-card-${doctor.id}`}
      >
        <div className="p-5">
          {doctor.is_premium && (
            <div className="mb-4 rounded-2xl bg-[#1C4E42] p-3 text-white shadow-sm" data-testid="premium-card-header">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#F0C86A]">Perfil em destaque</p>
                  <p className="mt-0.5 text-xs text-white/65">Mais informações para ajudar na escolha do paciente</p>
                </div>
                <span className="rounded-full bg-white/12 px-3 py-1 text-xs font-semibold text-white">Premium</span>
              </div>
            </div>
          )}

          <div className="flex items-start gap-4">
            <div className="w-16 h-16 sm:w-20 sm:h-20 bg-slate-100 rounded-xl flex items-center justify-center overflow-hidden flex-shrink-0">
              {doctor.photo ? (
                <img src={doctor.photo} alt={doctor.full_name} className="w-full h-full object-cover" />
              ) : (
                <User className="w-8 h-8 text-slate-400" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                {doctor.is_premium && (
                  <Badge className="text-xs bg-amber-50 text-amber-700 border border-amber-200" data-testid="badge-premium">
                    <Star className="w-3 h-3 mr-1 fill-amber-400 text-amber-400" />
                    Destaque
                  </Badge>
                )}
                {hasPlans ? (
                  <Badge className="text-xs bg-emerald-50 text-emerald-700 border border-emerald-200" data-testid="badge-has-plans">
                    <Shield className="w-3 h-3 mr-1" />
                    Aceita convênio
                  </Badge>
                ) : (
                  <Badge variant="outline" className="text-xs text-slate-500 border-slate-200" data-testid="badge-no-plans">
                    Particular
                  </Badge>
                )}
                {doctor.has_online_booking && (
                  <Badge className="badge-online text-xs">Agenda Online</Badge>
                )}
                {profileSignals >= 4 && (
                  <Badge className="text-xs bg-blue-50 text-blue-700 border border-blue-200" data-testid="badge-profile-complete">
                    <CheckCircle2 className="w-3 h-3 mr-1" />
                    Perfil completo
                  </Badge>
                )}
              </div>
              <h3 className="font-semibold text-slate-900 text-base md:text-lg leading-snug group-hover:text-primary transition-colors">
                {doctor.full_name}
              </h3>
              <p className="text-sm text-slate-600">{doctor.specialty_name}</p>
              {doctor.subspecialty && (
                <p className="text-xs text-slate-500">{doctor.subspecialty}</p>
              )}
              {registrationNumber && (
                <p className="text-sm text-slate-500 mt-1">
                  {registrationType || 'Registro'} {registrationNumber}{registrationUf ? `/${registrationUf}` : ''}
                </p>
              )}
            </div>
          </div>

          {/* Health Plans Badges */}
          {hasPlans && (
            <div className="flex flex-wrap gap-1 mt-3">
              {doctor.accepted_health_plans.slice(0, 4).map((plan, i) => (
                <Badge key={plan} variant="secondary" className="text-xs px-2 py-0.5 bg-emerald-50/50 text-emerald-600 border-emerald-100">
                  {plan}
                </Badge>
              ))}
              {doctor.accepted_health_plans.length > 4 && (
                <Badge variant="secondary" className="text-xs px-2 py-0.5">
                  +{doctor.accepted_health_plans.length - 4}
                </Badge>
              )}
            </div>
          )}

          {matchReasons.length > 0 && (
            <div className="mt-3 rounded-xl border border-emerald-100 bg-[#F5FAF8] px-3 py-2" data-testid={`match-reasons-${doctor.id}`}>
              <p className="text-xs font-semibold text-emerald-800 mb-1.5">{"Por que apareceu para voc\u00ea"}</p>
              <div className="flex flex-wrap gap-1.5">
                {matchReasons.map((reason) => (
                  <span key={reason} className="rounded-full bg-white px-2 py-0.5 text-[11px] text-emerald-700 border border-emerald-100">
                    {reason}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Address */}
          {(doctor.address || doctor.provider_name || doctor.city) && (
            <div className="flex items-start gap-2 mt-3 pt-3 border-t border-slate-100">
              <MapPin className="w-4 h-4 text-slate-400 flex-shrink-0 mt-0.5" />
              <div className="min-w-0">
                {(doctor.address || doctor.provider_name) && (
                  <span className="text-sm text-slate-600 block truncate">{doctor.address || doctor.provider_name}</span>
                )}
                {(doctor.city || doctor.state) && (
                  <span className="text-xs text-slate-500">{formatLocation(doctor.city, doctor.state)}</span>
                )}
              </div>
            </div>
          )}

          {/* Phone */}
          {doctor.phone && (
            <div className="flex items-center gap-2 mt-1">
              <Phone className="w-4 h-4 text-slate-400 flex-shrink-0" />
              <span className="text-sm text-primary font-medium">{doctor.phone}</span>
            </div>
          )}

          <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-100">
            <div className="flex items-center gap-1">
              {doctor.rating_avg > 0 ? (
                <>
                  <Star className="w-4 h-4 text-amber-400 fill-amber-400" />
                  <span className="text-sm font-medium">{doctor.rating_avg.toFixed(1)}</span>
                  <span className="text-xs text-slate-500">({doctor.rating_count} avaliações)</span>
                </>
              ) : (
                <span className="text-sm text-slate-500">Sem avaliações</span>
              )}
            </div>
            <div className="flex items-center gap-2">
              {(doctor.maps_url || doctor.address || doctor.city) && (
                <a
                  href={doctor.maps_url || `https://www.google.com/maps/search/${encodeURIComponent([doctor.full_name, doctor.address, doctor.city, doctor.state].filter(Boolean).join(', '))}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="flex items-center gap-1 text-xs text-slate-500 hover:text-primary transition-colors px-2 py-1.5 rounded-full hover:bg-primary/5"
                  data-testid={`map-btn-${doctor.id}`}
                >
                  <ExternalLink className="w-3.5 h-3.5" />
                  Mapa
                </a>
              )}
              {/* WhatsApp button on search card — PREMIUM-only feature.
                  Falls back to `phone` when dedicated `whatsapp` field is empty. */}
              {doctor.is_premium && (doctor.whatsapp || doctor.phone) && (
                <a
                  href={`https://wa.me/${(doctor.whatsapp || doctor.phone).replace(/\D/g, '').replace(/^(\d{10,11})$/, '55$1')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => {
                    e.stopPropagation();
                    logProfileEvent('whatsapp_click');
                  }}
                  className="flex items-center gap-1 text-xs text-green-700 border border-green-100 bg-green-50 hover:bg-green-100 transition-colors px-2.5 py-1.5 rounded-full"
                  data-testid={`whatsapp-btn-${doctor.id}`}
                >
                  <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/><path d="M12 0C5.373 0 0 5.373 0 12c0 2.625.846 5.059 2.284 7.034L.789 23.492a.75.75 0 00.913.914l4.458-1.495A11.952 11.952 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 22c-2.37 0-4.567-.826-6.29-2.2l-.44-.352-3.18 1.066 1.066-3.18-.352-.44A9.95 9.95 0 012 12C2 6.486 6.486 2 12 2s10 4.486 10 10-4.486 10-10 10z"/></svg>
                  WhatsApp
                </a>
              )}
              <span className="rounded-full bg-primary px-3 py-1.5 text-xs font-semibold text-white transition-colors group-hover:bg-[#2D6D5D]">Ver perfil</span>
            </div>
          </div>
        </div>
      </Link>
    );
  };

  const LoadingSkeleton = () => (
    <div className="bg-white rounded-2xl border border-slate-100 p-6">
      <div className="flex items-start gap-4">
        <Skeleton className="w-20 h-20 rounded-xl" />
        <div className="flex-1">
          <Skeleton className="h-4 w-20 mb-2" />
          <Skeleton className="h-6 w-48 mb-2" />
          <Skeleton className="h-4 w-32 mb-1" />
          <Skeleton className="h-4 w-24" />
        </div>
      </div>
      <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-100">
        <Skeleton className="h-4 w-32" />
        <Skeleton className="h-5 w-20" />
      </div>
    </div>
  );

  return (
    <div id="search-page-top" className="min-h-screen bg-slate-50 pb-20 lg:pb-0" data-testid="search-page">
      <SEOHead
        title={searchTitle}
        description={searchDescription}
        canonicalUrl={searchCanonical}
        imageUrl={searchImage}
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'CollectionPage',
          name: searchTitle,
          description: searchDescription,
        }}
      />
      <Navbar />

      <div className="container-app py-4 md:py-8">
        <div className="flex gap-6 lg:gap-8" style={{ height: 'calc(100vh - 6rem)' }}>
          {/* Desktop Filters Sidebar */}
          <aside className="hidden lg:block w-72 flex-shrink-0 overflow-y-auto pr-2 scrollbar-thin">
            <div className="bg-white rounded-2xl border border-slate-100 p-6">
              <div className="flex items-center gap-2 mb-6">
                <SlidersHorizontal className="w-5 h-5 text-primary" />
                <h2 className="font-semibold text-lg">Filtros</h2>
              </div>
              {renderFilters()}
            </div>
          </aside>

          {/* Main Content */}
          <main ref={resultsScrollRef} className="flex-1 overflow-y-auto pr-2 scrollbar-thin">
            {/* Header */}
            <div className="flex items-start justify-between mb-4 md:mb-6 gap-3">
              <div className="min-w-0">
                <h1 className="text-lg md:text-2xl font-bold text-[#1A2421]" style={{fontFamily:'Outfit,sans-serif'}}>Resultados da Busca</h1>
                <p className="text-sm text-[#59706A] mt-0.5" data-testid="results-count">
                  {loading
                    ? 'Buscando...'
                    : shouldShowResultCount
                      ? `${totalResults} ${totalResults === 1 ? 'profissional encontrado' : 'profissionais encontrados'}`
                      : 'Use os filtros para encontrar profissionais por especialidade e localidade.'}
                </p>
              </div>

              {/* Mobile Filters Button */}
              <Sheet open={mobileFiltersOpen} onOpenChange={setMobileFiltersOpen}>
                <SheetTrigger asChild>
                  <Button variant="outline" size="sm" className="lg:hidden flex-shrink-0" data-testid="mobile-filters-btn">
                    <Filter className="w-4 h-4 mr-1.5" />
                    Filtros
                    {hasActiveFilters && <Badge className="ml-1.5 bg-primary text-white h-5 w-5 p-0 flex items-center justify-center text-[10px]">!</Badge>}
                  </Button>
                </SheetTrigger>
                <SheetContent side="right" className="w-[320px] overflow-y-auto">
                  <SheetHeader>
                    <SheetTitle className="flex items-center gap-2">
                      <SlidersHorizontal className="w-5 h-5 text-primary" />
                      Filtros
                    </SheetTitle>
                  </SheetHeader>
                  <div className="mt-6">
                    {renderFilters(true)}
                  </div>
                </SheetContent>
              </Sheet>
            </div>

            {/* Active Filters Tags */}
            {hasActiveFilters && (
              <div className="flex flex-wrap gap-1.5 md:gap-2 mb-4 md:mb-6">
                {symptom && (
                  <Badge variant="secondary" className="px-2 md:px-3 py-0.5 md:py-1 text-xs">
                    {symptom}
                    <X className="w-3 h-3 ml-1.5 cursor-pointer" onClick={() => setSymptom('')} />
                  </Badge>
                )}
                {selectedSpecialty && selectedSpecialty !== 'all' && (
                  <Badge variant="secondary" className="px-2 md:px-3 py-0.5 md:py-1 text-xs">
                    {specialties.find(s => s.id === selectedSpecialty)?.name}
                    <X className="w-3 h-3 ml-1.5 cursor-pointer" onClick={() => setSelectedSpecialty('')} />
                  </Badge>
                )}
                {selectedOperator && (
                  <Badge variant="secondary" className="px-2 md:px-3 py-0.5 md:py-1 text-xs max-w-[160px] truncate">
                    {selectedOperator.name}
                    <X className="w-3 h-3 ml-1.5 cursor-pointer flex-shrink-0" onClick={() => setSelectedOperator(null)} />
                  </Badge>
                )}
                {selectedState && selectedState !== 'all' && (
                  <Badge variant="secondary" className="px-2 md:px-3 py-0.5 md:py-1 text-xs">
                    {states.find(s => s.uf === selectedState)?.name || selectedState}
                    <X className="w-3 h-3 ml-1.5 cursor-pointer" onClick={() => setSelectedState('')} />
                  </Badge>
                )}
                {selectedCity && selectedCity !== 'all' && (
                  <Badge variant="secondary" className="px-2 md:px-3 py-0.5 md:py-1 text-xs">
                    {selectedCity}
                    <X className="w-3 h-3 ml-1.5 cursor-pointer" onClick={() => setSelectedCity('')} />
                  </Badge>
                )}
                <Button variant="ghost" size="sm" onClick={clearFilters} className="text-slate-500 text-xs h-6">
                  Limpar todos
                </Button>
              </div>
            )}

            {(safetyAlert || suggestedSpecialties.length > 0 || searchError) && (
              <div className="mb-4 space-y-3" data-testid="central-search-guidance">
                {searchError && (
                  <div className="flex items-start gap-3 rounded-xl border border-red-100 bg-red-50 px-4 py-3">
                    <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-red-500" />
                    <p className="text-sm text-red-700">{searchError}</p>
                  </div>
                )}
                {safetyAlert && (
                  <div className="flex items-start gap-3 rounded-xl border border-amber-100 bg-amber-50 px-4 py-3">
                    <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-600" />
                    <p className="text-sm text-amber-800">{safetyAlert}</p>
                  </div>
                )}
                {suggestedSpecialties.length > 0 && (
                  <div className="rounded-xl border border-emerald-100 bg-white px-4 py-3">
                    <p className="mb-1 text-xs font-semibold uppercase text-emerald-700">Especialidades sugeridas</p>
                    <p className="mb-3 text-sm text-slate-600">Selecione uma especialidade para buscar médicos compatíveis.</p>
                    <div className="grid gap-2 md:grid-cols-2">
                      {suggestedSpecialties.map((item) => (
                        <button
                          key={item.id || item.name}
                          type="button"
                          onClick={() => {
                            if (item.id) setSelectedSpecialty(item.id);
                            if (item.name) {
                              const params = new URLSearchParams(searchParams);
                              if (item.id) params.set('specialty_id', item.id);
                              params.set('specialty_name', item.name);
                              setSearchParams(params);
                              setCurrentPage(1);
                            }
                          }}
                          className="rounded-xl border border-emerald-100 bg-emerald-50/80 p-3 text-left transition-colors hover:bg-emerald-100"
                        >
                          <span className="flex items-center justify-between gap-3">
                            <span className="font-semibold text-emerald-900">{item.name}</span>
                            {typeof item.confidence === 'number' && (
                              <span className="rounded-full bg-white px-2 py-0.5 text-xs font-medium text-emerald-700">
                                {Math.round(item.confidence * 100)}%
                              </span>
                            )}
                          </span>
                          {(item.explanation || item.reason) && (
                            <span className="mt-1 block text-xs leading-relaxed text-slate-600">
                              {item.explanation || item.reason}
                            </span>
                          )}
                          {(item.matchedKeywords || item.signals || []).length > 0 && (
                            <span className="mt-2 flex flex-wrap gap-1">
                              {(item.matchedKeywords || item.signals || []).slice(0, 3).map((keyword) => (
                                <span key={keyword} className="rounded-full bg-white px-2 py-0.5 text-[11px] text-emerald-700">
                                  {keyword}
                                </span>
                              ))}
                            </span>
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Refinement Filters - filter loaded results */}
            {!loading && doctors.length > 0 && (
              <div className="mb-4 flex flex-wrap items-center gap-2 py-2.5 px-3 bg-slate-50/80 rounded-lg border border-slate-100" data-testid="refinement-filters">
                <span className="text-xs font-medium text-slate-500 mr-1">Refinar:</span>
                <button
                  onClick={() => setFilterRating(filterRating === '4' ? '' : '4')}
                  className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${filterRating === '4' ? 'bg-amber-100 border-amber-300 text-amber-800' : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'}`}
                  data-testid="refine-rating-4"
                >
                  <Star className="w-3 h-3 inline mr-1 -mt-0.5" />4+
                </button>
                <button
                  onClick={() => setFilterRating(filterRating === '3' ? '' : '3')}
                  className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${filterRating === '3' ? 'bg-amber-100 border-amber-300 text-amber-800' : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'}`}
                  data-testid="refine-rating-3"
                >
                  <Star className="w-3 h-3 inline mr-1 -mt-0.5" />3+
                </button>
                <button
                  onClick={() => setFilterHasPlans(!filterHasPlans)}
                  className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${filterHasPlans ? 'bg-emerald-100 border-emerald-300 text-emerald-800' : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'}`}
                  data-testid="refine-has-plans"
                >
                  <Shield className="w-3 h-3 inline mr-1 -mt-0.5" />Aceita convênio
                </button>
                <button
                  onClick={() => setFilterHasPhone(!filterHasPhone)}
                  className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${filterHasPhone ? 'bg-blue-100 border-blue-300 text-blue-800' : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'}`}
                  data-testid="refine-has-phone"
                >
                  <MapPin className="w-3 h-3 inline mr-1 -mt-0.5" />Com telefone
                </button>
                {(filterRating || filterHasPlans || filterHasPhone) && (
                  <button
                    onClick={() => { setFilterRating(''); setFilterHasPlans(false); setFilterHasPhone(false); }}
                    className="text-xs text-slate-400 hover:text-slate-600 ml-1"
                    data-testid="refine-clear"
                  >
                    Limpar
                  </button>
                )}
              </div>
            )}


            {/* Results Grid */}
            {loading ? (
              <div className="grid xl:grid-cols-2 gap-4 md:gap-5">
                {[...Array(6)].map((_, i) => <LoadingSkeleton key={`skeleton-${i}`} />)}
              </div>
            ) : filteredDoctors.length > 0 ? (
              <>
                {/* Plans summary indicator */}
                {(() => {
                  const withPlans = filteredDoctors.filter(d => d.accepted_health_plans && d.accepted_health_plans.length > 0).length;
                  const withoutPlans = filteredDoctors.length - withPlans;
                  return (
                    <div className="flex items-center gap-4 mb-5 p-3 bg-white rounded-xl border border-slate-100" data-testid="plans-summary">
                      <Shield className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                      <div className="flex items-center gap-3 text-sm flex-wrap">
                        <span className="flex items-center gap-1.5">
                          <span className="w-2.5 h-2.5 rounded-full bg-emerald-500" />
                          <span className="font-medium text-emerald-700">{withPlans}</span>
                          <span className="text-slate-500">com convênio</span>
                        </span>
                        <span className="text-slate-300">|</span>
                        <span className="flex items-center gap-1.5">
                          <span className="w-2.5 h-2.5 rounded-full bg-slate-300" />
                          <span className="font-medium text-slate-600">{withoutPlans}</span>
                          <span className="text-slate-500">sem info</span>
                        </span>
                      </div>
                      {withPlans > 0 && (
                        <div className="ml-auto hidden sm:block">
                          <div className="flex h-2 w-24 rounded-full overflow-hidden bg-slate-200">
                            <div className="bg-emerald-500 rounded-full" style={{width: `${(withPlans / filteredDoctors.length) * 100}%`}} />
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })()}

                {/* Active refinement info */}
                {(filterRating || filterHasPlans || filterHasPhone) && filteredDoctors.length !== doctors.length && (
                  <p className="text-xs text-slate-500 mb-3">Mostrando {filteredDoctors.length} de {doctors.length} profissionais (filtros de refinamento ativos)</p>
                )}

                <div className="grid xl:grid-cols-2 gap-4 md:gap-5">
                  {filteredDoctors.map((doctor) => <DoctorCard key={doctor.id} doctor={doctor} />)}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex justify-center gap-2 mt-8">
                    <Button variant="outline" disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)}>Anterior</Button>
                    <span className="flex items-center px-4 text-sm text-slate-600">Página {currentPage} de {totalPages}</span>
                    <Button variant="outline" disabled={currentPage === totalPages} onClick={() => setCurrentPage(p => p + 1)}>Próxima</Button>
                  </div>
                )}
              </>
            ) : (
              /* Show "no results" - differentiate between refinement filter empty vs truly empty */
              doctors.length > 0 && filteredDoctors.length === 0 ? (
                <div className="text-center py-10">
                  <div className="w-14 h-14 bg-amber-50 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Filter className="w-7 h-7 text-amber-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-800 mb-1">Nenhum resultado com estes filtros</h3>
                  <p className="text-sm text-slate-500 mb-3">Existem {doctors.length} profissionais, mas nenhum atende os filtros de refinamento.</p>
                  <Button variant="outline" size="sm" onClick={() => { setFilterRating(''); setFilterHasPlans(false); setFilterHasPhone(false); }} data-testid="clear-refinements">
                    Limpar filtros de refinamento
                  </Button>
                </div>
              ) : (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Search className="w-8 h-8 text-slate-400" />
                  </div>
                  <h3 className="text-xl font-semibold text-slate-900 mb-2">Nenhum profissional encontrado</h3>
                  <p className="text-slate-600 mb-4">
                    {emptyStateSuggestion?.message || 'Não encontramos profissionais na base para estes critérios.'}
                  </p>
                  <Button onClick={triggerWebSearch} variant="outline" size="sm" data-testid="retry-search-empty">
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Buscar novamente
                  </Button>
                </div>
              )
            )}

          </main>
        </div>
      </div>

      <div className="fixed inset-x-0 bottom-0 z-40 border-t border-slate-200 bg-white/95 px-4 py-3 shadow-[0_-10px_28px_rgba(15,23,42,0.08)] backdrop-blur lg:hidden">
        <div className="mx-auto flex max-w-lg gap-2">
          <Button
            variant="outline"
            className="flex-1 rounded-full"
            onClick={() => setMobileFiltersOpen(true)}
            data-testid="mobile-sticky-filters-btn"
          >
            <Filter className="mr-2 h-4 w-4" />
            Filtros
          </Button>
          <Button
            className="flex-1 rounded-full bg-primary"
            onClick={startNewSearch}
            data-testid="mobile-sticky-new-search-btn"
          >
            Nova busca
          </Button>
        </div>
      </div>

      <Footer />
    </div>
  );
}





